import { yupResolver } from '@hookform/resolvers/yup';
import cls from 'classnames';
import React, { memo, useCallback, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import Select from 'react-select';

import styles from './Details.module.css';
import Step from '../Step/Step';
import { ConfirmProps, StepType } from '../../../state/types';
import { State, useState } from '../../../state/store';
import { detailsSchema } from '../../../state/validation';
import FormContainer from '../../FormContainer/FormContainer';
import { commonSelectStyles } from '../../../state/constants';
import QuestionTooltip from '../../QuestionTooltip/QuestionTooltip';
import useStateRef from 'react-usestateref';

interface DisabledFieldProps {
  id: string;
  label: string;
  value: string;
  serverErrors: string[];
}

const DisabledField = memo(({ id, label, value, serverErrors }: DisabledFieldProps) => {
  const fieldId = `inline-${id}`;

  return (
    <div className="md:flex md:items-center mb-6">
      <div className="md:w-1/3">
        <label className="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4" htmlFor={fieldId}>
          {label}:
        </label>
      </div>
      <div className="md:w-2/3">
        <div>
          <input
            value={value}
            disabled={true}
            className={cls(
              'appearance-none border-2 border-gray-200 rounded w-full py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500',
              'disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none'
            )}
            id={fieldId}
            type="text"
          />
        </div>
        <div className="pt-2 text-red-400">
          {serverErrors.map((serverError, index) => (
            <div key={index}>{serverError}</div>
          ))}
        </div>
      </div>
    </div>
  );
});

interface FieldProps {
  label: string;
  error?: string;
  serverErrors: string[];
  register: () => {
    name: string; // TODO: add better type
  };
  onBlur: (event: any) => void;
  onCut?: (event: any) => void;
  onCopy?: (event: any) => void;
  tabIndex?: number;
}

const Field = memo(({ label, register, error, serverErrors, onBlur, onCut, onCopy, tabIndex }: FieldProps) => {
  const registerProps = { ...register() };
  const id = `inline-${registerProps.name}`;
  return (
    <div className="md:flex md:items-center mb-6">
      <div className="md:w-1/3">
        <label className="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4" htmlFor={id}>
          {label}:
        </label>
      </div>
      <div className="md:w-2/3">
        <div>
          <input
            {...registerProps}
            onBlur={onBlur}
            className="appearance-none border-2 border-gray-200 rounded w-full py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500"
            id={id}
            type="text"
            tabIndex={tabIndex}
            onCut={onCut}
            onCopy={onCopy}
          />
        </div>
        <div className="pt-2 text-red-400">
          <div key="form-error">{error}</div>
          {serverErrors.map((serverError, index) => (
            <div key={index}>{serverError}</div>
          ))}
        </div>
      </div>
    </div>
  );
});

// Devices for which it is important working condition for trade-in device
// const qualifyingDevices = ['DM1', 'DM2', 'Dm3'];

const Details: React.FC = memo(() => {
  const {
    step,
    model,
    modelsMyBrand,
    tradeInValue,
    tradeInValueBad,
    imei,
    imeiOld,
    brand,
    email,
    confirmEmail,
    firstName,
    lastName,
    postalCode,
    addressLine1,
    addressLine2,
    city,
    country,
    county,
    iban,
    countries,
    counties,
    orderErrors,
    orderErrorMessage,
    isDeviceWorking,
    isVendorUnlocked,
    isTermsConfirmed,
    requestSent,
    purchasedPhone,
    eligibleDevices,
    promotions,
    promoDevices,
    dispatch,
  } = useState(
    'step',
    'model',
    'modelsMyBrand',
    'imeiOld',
    'brand',
    'imei',
    'tradeInValue',
    'tradeInValueBad',
    'email',
    'firstName',
    'lastName',
    'postalCode',
    'addressLine1',
    'addressLine2',
    'city',
    'country',
    'county',
    'countries',
    'counties',
    'iban',
    'confirmEmail',
    'orderErrors',
    'orderErrorMessage',
    'isDeviceWorking',
    'isVendorUnlocked',
    'isTermsConfirmed',
    'requestSent',
    'purchasedPhone',
    'eligibleDevices',
    'promotions',
    'promoDevices'
  );
  const {
    register,
    trigger,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<State>({
    /**
     * Required to check default values on reload
     * https://github.com/react-hook-form/react-hook-form/discussions/2348
     */
    mode: 'onChange',
    resolver: yupResolver(detailsSchema),
    defaultValues: {
      firstName: firstName,
      email: email,
      confirmEmail: confirmEmail,
      lastName: lastName,
      postalCode: postalCode,
      addressLine1: addressLine1,
      addressLine2: addressLine2,
      city: city,
      country: country,
      iban: iban,
    },
  });

  const [tradeInPromoValue, setTradeInPromoValue] = useStateRef(0);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('DetailsStep');
    dispatch('update', {
      recaptchaToken: token,
    });
  }, [executeRecaptcha, dispatch]);

  useEffect(() => {
    if (step === StepType.Details && !requestSent) {
      handleReCaptchaVerify().then();
    }
  }, [handleReCaptchaVerify, step, requestSent]);

  const prepareRegisterCheckboxProps = useCallback(
    (name: keyof State, options = { required: true }) => {
      const registerProps = { ...register(name, options) };
      const originalOnChange = registerProps.onChange;

      registerProps.onChange = (e) => {
        /**
         * Trigger validation right when user is typing and update state in case there are no errors
         */
        return originalOnChange(e)
          .then(() => trigger(name))
          .then(() => {
            if (!errors[name]) {
              dispatch('update', {
                [name]: watch(name),
              });
            }
          });
      };
      return registerProps;
    },
    [register, dispatch, errors, trigger, watch]
  );

  const prepareRegisterProps = useCallback(
    (name: keyof State, triggerFields: ReadonlyArray<keyof State> = []) => {
      return () => {
        const registerProps = { ...register(name) };
        const onChange = registerProps.onChange;
        registerProps.onChange = (e) => {
          return onChange(e)
            .then(() => {
              if (!errors[name]) {
                dispatch('update', {
                  [name]: e.target.value,
                });
              }
            })
            .then(() => {
              triggerFields.forEach((key) => trigger(key));
            });
        };
        return registerProps;
      };
    },
    [dispatch, errors, register, trigger]
  );

  const Confirm = ({ name, value, text }: ConfirmProps) => {
    setValue(name, value);
    const confirmProps = { ...prepareRegisterCheckboxProps(name, { required: true }) };
    return (
      <div
        className={'pb-3'}
        onClick={() => {
          const reversed = !value;
          dispatch('update', {
            [name]: reversed,
          });
        }}
      >
        <input type={'checkbox'} className={styles.checkbox} {...confirmProps} onChange={undefined} />
        <label className={cls('cursor-pointer inline', styles.label)}>{text}</label>
      </div>
    );
  };

  const onFieldBlur = useCallback((fieldName: keyof State) => () => trigger(fieldName), [trigger]);

  useEffect(() => {
    if (email) {
      setValue('email', email);
      onFieldBlur('confirmEmail')();
    }
  }, [email, setValue, onFieldBlur]);

  const error = (key: keyof State) => {
    // TODO: get better types
    const obj = (errors[key] as { type: string; message: string }) || undefined;
    return obj?.type === 'required' ? obj?.message || 'Field is required' : obj?.message || '';
  };

  const handleCopy = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  setValue('country', country);
  setValue('county', county);
  prepareRegisterProps('country');
  prepareRegisterProps('county');

  const countryOption = country ? countries.find((c) => c.value === country) : null;
  const countyOption = county ? counties.find((c) => c.value === county) : null;

  const modelOption = model ? modelsMyBrand.find((p) => p.value === model) : null;

  const isQualifyingDevice =
    //purchasedPhone && qualifyingDevices.includes(purchasedPhone) && brand !== 'Any Other Smartphone';
    purchasedPhone && brand !== 'Any Other Smartphone';

  const tradeInValueFinal = isQualifyingDevice
    ? isDeviceWorking && isVendorUnlocked
      ? tradeInValue
      : tradeInValueBad
    : 0;
  const tradeInValueTotal = (+tradeInPromoValue + +tradeInValueFinal).toFixed(2);

  useEffect(() => {
    promoDevices.forEach((promoDevice) => {
      if (promoDevice.alias === purchasedPhone && promotions.is_enhancement_active) {
        setTradeInPromoValue(+promoDevice.promo_price);
        eligibleDevices.forEach((device) => {
          if (device.id === +model) {
            setTradeInPromoValue(+promoDevice.promo_price + +promoDevice.promo_price_enhancement);
          }
        });
      }
    });
  }, [dispatch, setTradeInPromoValue, model, purchasedPhone, eligibleDevices, promoDevices]);

  return (
    <Step
      stepToShow={StepType.Details}
      stepBack={StepType.OldDeviceStatus}
      stepNext={StepType.Confirmation}
      isValid={isValid && !requestSent}
    >
      <div className={styles.Details} data-testid="ContactDetails">
        <form className="w-full">
          <div className="bg-gray-100 rounded">
            <FormContainer header={'Your Trade-in Details'}>
              <div className={styles.TradeIdTotalBlock}>
                <h1 className="text-xl text-center pt-6">TRADE-IN TOTAL</h1>
                <div className={`${styles.TradeIdTotal} text-blue-500`}>&euro; {tradeInValueTotal}</div>
              </div>
              <DisabledField id="vendor" label="Vendor" value={brand} serverErrors={orderErrors['vendor'] || []} />
              <DisabledField
                id="model"
                label="Model"
                value={modelOption?.label || model || ''}
                serverErrors={orderErrors['phone_id'] || []}
              />
              <DisabledField
                id="imei"
                label="Purchased Device IMEI"
                value={imei}
                serverErrors={orderErrors['sold_imei'] || []}
              />
              <DisabledField
                id="imei-old"
                label="Trade-in Device IMEI"
                value={imeiOld}
                serverErrors={orderErrors['tradein_imei'] || []}
              />
            </FormContainer>
          </div>

          <div className="bg-gray-100 rounded">
            <FormContainer header={'Contact Details'}>
              <div className="md:flex md:items-center">
                <div className="md:w-1/2">
                  <Field
                    error={error('firstName')}
                    serverErrors={orderErrors['first_name'] || []}
                    label={'First Name *'}
                    onBlur={onFieldBlur('firstName')}
                    register={prepareRegisterProps('firstName')}
                    tabIndex={1}
                  />
                </div>
                <div className="md:w-1/2">
                  <Field
                    error={error('lastName')}
                    serverErrors={orderErrors['last_name'] || []}
                    label={'Last Name *'}
                    onBlur={onFieldBlur('lastName')}
                    register={prepareRegisterProps('lastName')}
                    tabIndex={2}
                  />
                </div>
              </div>
              <div className="md:flex md:items-center">
                <div className="md:w-1/2">
                  <Field
                    error={error('email')}
                    serverErrors={[]}
                    label={'Email *'}
                    onBlur={onFieldBlur('email')}
                    register={prepareRegisterProps('email')}
                    onCopy={handleCopy}
                    onCut={handleCopy}
                    tabIndex={3}
                  />
                </div>
                <div className="md:w-1/2">
                  <Field
                    error={error('confirmEmail')}
                    serverErrors={[]}
                    label={'Confirm Email *'}
                    onBlur={onFieldBlur('confirmEmail')}
                    register={prepareRegisterProps('confirmEmail')}
                    tabIndex={4}
                  />
                </div>
              </div>
              <div className="md:flex md:items-center">
                <div className="md:w-1/2">
                  <Field
                    error={error('addressLine1')}
                    serverErrors={orderErrors['address'] || []}
                    label={'Address Line 1 *'}
                    onBlur={onFieldBlur('addressLine1')}
                    register={prepareRegisterProps('addressLine1')}
                    tabIndex={5}
                  />
                </div>
                <div className="md:w-1/2">
                  <Field
                    error={error('addressLine2')}
                    serverErrors={[]}
                    label={'Address Line 2'}
                    onBlur={onFieldBlur('addressLine2')}
                    register={prepareRegisterProps('addressLine2')}
                    tabIndex={6}
                  />
                </div>
              </div>
              <div className="md:flex md:items-center">
                <div className="md:w-1/2">
                  <Field
                    error={error('postalCode')}
                    serverErrors={orderErrors['postal_code'] || []}
                    label={'Postcode *'}
                    onBlur={onFieldBlur('postalCode')}
                    register={prepareRegisterProps('postalCode')}
                    tabIndex={7}
                  />
                </div>
                <div className="md:w-1/2">
                  <Field
                    error={error('city')}
                    serverErrors={orderErrors['city'] || []}
                    label={'City *'}
                    onBlur={onFieldBlur('city')}
                    register={prepareRegisterProps('city')}
                    tabIndex={8}
                  />
                </div>
              </div>
              <div className="md:flex md:items-center">
                <div className="md:w-1/2">
                  <div className="md:flex md:items-center mb-6">
                    <div className="md:w-1/3">
                      <label
                        className="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4"
                        htmlFor="country"
                      >
                        Country *:
                      </label>
                    </div>
                    <div className="md:w-2/3">
                      <Select
                        inputId="country"
                        value={countryOption}
                        onBlur={onFieldBlur('country')}
                        options={countries}
                        isDisabled={true}
                        tabIndex={9}
                        styles={commonSelectStyles}
                      />
                      <div className="pt-2 text-red-400">
                        <div key="message-error">{error('country')}</div>
                        {(orderErrors['country'] || []).map((serverError, index) => (
                          <div key={index}>{serverError}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="md:w-1/2">
                  <div className="md:flex md:items-center mb-6">
                    <div className="md:w-1/3">
                      <label className="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4" htmlFor="county">
                        County *:
                      </label>
                    </div>
                    <div className="md:w-2/3">
                      <Select
                        inputId="county"
                        value={countyOption}
                        onBlur={onFieldBlur('county')}
                        onChange={(option) => {
                          setValue('county', option?.value || '', { shouldValidate: true });
                          dispatch('update', {
                            county: option?.value || '',
                          });
                        }}
                        options={counties}
                        tabIndex={10}
                        styles={commonSelectStyles}
                      />
                      <div className="pt-2 text-red-400">
                        <div key="message-error">{error('county')}</div>
                        {(orderErrors['county'] || []).map((serverError, index) => (
                          <div key={index}>{serverError}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FormContainer>
          </div>

          <div className="bg-gray-100 rounded">
            <FormContainer header={'Bank Account Details'}>
              <div className="md:flex md:items-center">
                <div className="md:w-full mx-auto">
                  <div className={'mx-auto max-w-xs pb-2'}>
                    <label className="block text-gray-500 font-bold text-center mb-1 md:mb-0 pr-4" htmlFor="iban">
                      Your Account IBAN *:
                    </label>
                  </div>
                  <div className={'flex mx-auto max-w-xs'}>
                    <div className={styles.imeiInput}>
                      <input
                        {...prepareRegisterProps('iban')()}
                        onBlur={onFieldBlur('iban')}
                        defaultValue={iban}
                        className={cls(
                          'appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500'
                        )}
                        id="iban"
                        type="text"
                        tabIndex={11}
                      />
                    </div>
                    <div className={styles.FieldQuestion}>
                      <QuestionTooltip>
                        <p className="p-2 m-0">Your IBAN is required in order to pay you for your trade-in device.</p>
                        <p className="p-2 m-0">
                          IBAN should be between 15 and at most 32 alphanumeric characters long.
                        </p>
                        <p className="p-2 m-0">In Ireland, the standard length of an IBAN is 22 characters.</p>
                      </QuestionTooltip>
                    </div>
                  </div>
                  <div className="pt-2 text-red-400 text-center">
                    <div key="message-error">{errors.iban?.message}</div>
                    {(errors.iban?.type === 'min' || errors.iban?.type === 'max') && (
                      <div key="custom-error">
                        In Ireland, the standard length of an IBAN is <strong>22 characters</strong>.
                      </div>
                    )}
                    {(orderErrors['iban'] || []).map((serverError, index) => (
                      <div key={index}>{serverError}</div>
                    ))}
                  </div>
                </div>
              </div>
            </FormContainer>
          </div>

          <div className="bg-gray-100 rounded">
            <FormContainer header={'Confirmation'}>
              <div className="md:flex md:items-center">
                <div className="md:w-full mx-auto">
                  <div className="md:flex md:items-center">
                    <div className="md:w-1/4">&nbsp;</div>
                    <div className="md:w-3/4">
                      <strong>
                        <Confirm
                          name={'isTermsConfirmed'}
                          value={isTermsConfirmed}
                          text={'I confirm that the above is correct and agree to the terms of the trade-in.'}
                        />
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </FormContainer>
          </div>
        </form>
        <div className="pt-2 text-red-400 text-center">{orderErrorMessage}</div>
        <div className="pt-2 text-center text-red-400">
          {(orderErrors['g_recaptcha_response'] || []).map((serverError, index) => (
            <div key={index}>{serverError}</div>
          ))}
        </div>
      </div>
    </Step>
  );
});

export default Details;
